import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface Branding {
  displayName: string | null;
  tagline: string | null;
  logoDataUri: string | null;
  accentColor: string | null;
  supportEmail: string | null;
  supportUrl: string | null;
  footerNote: string | null;
}

export interface BrandingResponse {
  effective: Branding;
  platform: Branding;
  org: Branding;
  orgId: string | null;
}

/** What the product is called when nobody has said otherwise. */
export const DEFAULT_BRAND = {
  displayName: 'SimpleACS',
  tagline: 'FTTH / PON platform',
  logo: '/logo.png',
} as const;

/**
 * The brand for the signed-in user: their organization's over the platform's,
 * field by field.
 *
 * Kept fresh for the whole session rather than re-fetched per page — a logo
 * that flickers in on every navigation looks like a bug, and this changes
 * about as often as a company renames itself.
 */
export function useBranding() {
  const query = useQuery({
    queryKey: ['branding'],
    queryFn: () => api.get<BrandingResponse>('/branding'),
    staleTime: 5 * 60_000,
  });
  useAccent(query.data?.effective.accentColor ?? null);
  return query;
}

/** The platform's brand alone, readable before anybody signs in. */
export function usePublicBranding() {
  const query = useQuery({
    queryKey: ['branding', 'public'],
    queryFn: () => api.get<Branding>('/branding/public'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  useAccent(query.data?.accentColor ?? null);
  return query;
}

/**
 * Paints the chosen colour over the theme's primary.
 *
 * Set on the document root rather than passed down as props, because primary
 * reaches buttons, links, focus rings and the active nav item through CSS
 * variables already — overriding the variable brands all of them at once and
 * leaves the light/dark switching intact.
 */
function useAccent(hex: string | null) {
  useEffect(() => {
    const root = document.documentElement;
    if (!hex) {
      root.style.removeProperty('--primary');
      root.style.removeProperty('--primary-hover');
      return;
    }
    root.style.setProperty('--primary', hex);
    // A hover shade derived from the same colour, so an operator picks one
    // value and still gets a button that responds to the pointer.
    root.style.setProperty('--primary-hover', shift(hex, -18));
    return () => {
      root.style.removeProperty('--primary');
      root.style.removeProperty('--primary-hover');
    };
  }, [hex]);
}

/** Moves a hex colour towards black (negative) or white (positive). */
function shift(hex: string, amount: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const channel = (shiftBy: number) =>
    Math.max(0, Math.min(255, ((n >> shiftBy) & 0xff) + amount))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(16)}${channel(8)}${channel(0)}`;
}
